import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { 
  Filter, X, ChevronDown, ChevronUp, MapPin, Briefcase, 
  IndianRupee, Building, Clock, GraduationCap, Search
} from 'lucide-react';

const EXPERIENCE_OPTIONS = [
  'Fresher',
  '0-1 years',
  '1-2 years',
  '2-3 years',
  '3-5 years',
  '5-7 years',
  '7-10 years',
  '10-15 years',
  '15+ years',
];

const SALARY_RANGES = [
  { label: '0-3 LPA', min: 0, max: 3 },
  { label: '3-6 LPA', min: 3, max: 6 },
  { label: '6-10 LPA', min: 6, max: 10 },
  { label: '10-15 LPA', min: 10, max: 15 },
  { label: '15-25 LPA', min: 15, max: 25 },
  { label: '25-50 LPA', min: 25, max: 50 },
  { label: '50+ LPA', min: 50, max: null },
];

const JOB_TYPES = [
  'Full Time',
  'Part Time',
  'Contract',
  'Internship',
  'Freelance',
  'Remote',
];

const POSTED_WITHIN = [
  { label: 'Last 24 hours', days: 1 },
  { label: 'Last 3 days', days: 3 },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 15 days', days: 15 },
  { label: 'Last 30 days', days: 30 },
];

const INDUSTRIES = [
  'IT Services & Consulting',
  'Software Product',
  'Internet',
  'Financial Services',
  'Manufacturing',
  'Healthcare',
  'Education',
  'E-commerce',
  'Telecom',
  'BFSI',
];

const JobFilters = ({ filters, onFilterChange, onClearFilters, totalJobs }) => {
  const [expandedSections, setExpandedSections] = useState({
    experience: true,
    salary: true,
    jobType: true,
    location: false,
    industry: false,
    postedWithin: false,
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleCheckboxChange = (filterType, value) => {
    const currentValues = filters[filterType] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    onFilterChange({ ...filters, [filterType]: newValues });
  };

  const handleInputChange = (filterType, value) => {
    onFilterChange({ ...filters, [filterType]: value });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.experience?.length) count += filters.experience.length;
    if (filters.salary?.length) count += filters.salary.length;
    if (filters.jobType?.length) count += filters.jobType.length;
    if (filters.industry?.length) count += filters.industry.length;
    if (filters.postedWithin) count += 1;
    if (filters.locationFilter) count += 1;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  const FilterSection = ({ title, icon: Icon, sectionKey, children }) => (
    <div className="border-b border-gray-200 py-3">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="flex items-center justify-between w-full text-left"
      >
        <span className="flex items-center gap-2 font-medium text-gray-800">
          <Icon className="w-4 h-4 text-gray-500" />
          {title}
        </span>
        {expandedSections[sectionKey] ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>
      {expandedSections[sectionKey] && (
        <div className="mt-3 space-y-2">
          {children}
        </div>
      )}
    </div>
  );

  const CheckboxItem = ({ label, checked, onChange }) => (
    <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
      />
      <span className="text-sm text-gray-600">{label}</span>
    </label>
  );

  return (
    <Card className="bg-white border border-gray-200 p-4 sticky top-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-blue-600" />
          <span className="font-semibold text-gray-800">Filters</span>
          {activeFilterCount > 0 && (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full">
              {activeFilterCount}
            </span>
          )}
        </div>
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8"
          >
            <X className="w-4 h-4 mr-1" />
            Clear All
          </Button>
        )}
      </div>

      {/* Results Count */}
      <div className="mb-4 p-2 bg-blue-50 rounded text-sm text-blue-700">
        <strong>{totalJobs}</strong> jobs found
      </div>

      {/* Location Filter */}
      <FilterSection title="Location" icon={MapPin} sectionKey="location">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Enter city or area"
            value={filters.locationFilter || ''}
            onChange={(e) => handleInputChange('locationFilter', e.target.value)}
            className="pl-8 h-9 text-sm"
          />
        </div>
      </FilterSection>

      {/* Experience Filter */}
      <FilterSection title="Experience" icon={Briefcase} sectionKey="experience">
        <div className="max-h-48 overflow-y-auto">
          {EXPERIENCE_OPTIONS.map((exp) => (
            <CheckboxItem
              key={exp}
              label={exp}
              checked={(filters.experience || []).includes(exp)}
              onChange={() => handleCheckboxChange('experience', exp)}
            />
          ))}
        </div>
      </FilterSection>

      {/* Salary Filter */}
      <FilterSection title="Salary" icon={IndianRupee} sectionKey="salary">
        <div className="max-h-48 overflow-y-auto">
          {SALARY_RANGES.map((range) => (
            <CheckboxItem
              key={range.label}
              label={range.label}
              checked={(filters.salary || []).includes(range.label)}
              onChange={() => handleCheckboxChange('salary', range.label)}
            />
          ))}
        </div>
      </FilterSection>

      {/* Job Type Filter */}
      <FilterSection title="Job Type" icon={Clock} sectionKey="jobType">
        <div className="flex flex-wrap gap-2">
          {JOB_TYPES.map((type) => (
            <button
              key={type}
              onClick={() => handleCheckboxChange('jobType', type)}
              className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                (filters.jobType || []).includes(type)
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Industry Filter */}
      <FilterSection title="Industry" icon={Building} sectionKey="industry">
        <div className="max-h-48 overflow-y-auto">
          {INDUSTRIES.map((ind) => (
            <CheckboxItem
              key={ind}
              label={ind}
              checked={(filters.industry || []).includes(ind)}
              onChange={() => handleCheckboxChange('industry', ind)}
            />
          ))}
        </div>
      </FilterSection>

      {/* Posted Within Filter */}
      <FilterSection title="Posted Within" icon={Clock} sectionKey="postedWithin">
        <div className="space-y-1">
          {POSTED_WITHIN.map((option) => (
            <label key={option.days} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
              <input
                type="radio"
                name="postedWithin"
                checked={filters.postedWithin === option.days}
                onChange={() => handleInputChange('postedWithin', option.days)}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">{option.label}</span>
            </label>
          ))}
          {filters.postedWithin && (
            <button
              onClick={() => handleInputChange('postedWithin', null)}
              className="text-xs text-blue-600 hover:underline mt-1"
            >
              Clear
            </button>
          )}
        </div>
      </FilterSection>
    </Card>
  );
};

export default JobFilters;
